// Volta — repack loose Solo ropes into the cheapest Duo/Team bundles.
// 2× Solo → Duo, 3× Solo → Team, Duo + Solo → Team, … Colours are kept (bundle variants
// are per colour mix, so stock still decrements per colour). Only runs when a Solo line is
// involved and the new combination is strictly cheaper, so it can never loop.
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const SOURCE = 'volta-bundle-optimizer';
const COLOR_A = 'Zwart & Bordeaux';
const COLOR_B = 'Bordeaux';

let variants = [];
try {
  variants = JSON.parse(document.getElementById('volta-bundle-data')?.textContent || '[]');
} catch (e) {
  variants = [];
}

// variant id → { size, price, colors: [...] }
const byId = new Map();
for (const v of variants) {
  let colors = [];
  if (v.size === 1) {
    colors = [v.title];
  } else {
    // "2× Zwart & Bordeaux + 1× Bordeaux"
    for (const part of v.title.split('+')) {
      const m = part.trim().match(/^(\d+)\s*[×x]\s*(.+)$/);
      if (m) for (let i = 0; i < Number(m[1]); i++) colors.push(m[2].trim());
    }
  }
  if (colors.length === v.size) byId.set(String(v.id), { ...v, colors });
}

const countOf = (colors, c) => colors.filter((x) => x === c).length;

function findVariant(size, colors) {
  const a = countOf(colors, COLOR_A);
  for (const v of byId.values()) {
    if (v.size === size && countOf(v.colors, COLOR_A) === a && countOf(v.colors, COLOR_B) === size - a) return v;
  }
  return null;
}

/** Cheapest packing of n ropes: as many Teams as possible, then a Duo or a Solo. */
function packSizes(n) {
  const sizes = Array(Math.floor(n / 3)).fill(3);
  if (n % 3 === 2) sizes.push(2);
  if (n % 3 === 1) sizes.push(1);
  return sizes;
}

/**
 * @param cart   AJAX cart ({ items })
 * @param extra  ropes that are about to be added but are not in the cart yet: [{ variant, quantity }]
 */
function buildPlan(cart, extra = []) {
  const lines = (cart?.items || []).filter((item) => {
    const v = byId.get(String(item.variant_id));
    return v && v.size <= 2 && !item.selling_plan_allocation;
  });
  const hasSolo =
    lines.some((item) => byId.get(String(item.variant_id)).size === 1) || extra.some((x) => x.variant.size === 1);
  if (!hasSolo) return null;

  const colors = [];
  let currentCost = 0;
  for (const item of lines) {
    const v = byId.get(String(item.variant_id));
    for (let q = 0; q < item.quantity; q++) colors.push(...v.colors);
    currentCost += item.original_line_price;
  }
  for (const x of extra) {
    for (let q = 0; q < x.quantity; q++) colors.push(...x.variant.colors);
    currentCost += x.variant.price * x.quantity;
  }
  if (colors.length < 2) return null;

  const packs = packColors(colors);
  if (!packs) return null;
  const planCost = packs.reduce((sum, p) => sum + p.variant.price, 0);
  if (planCost >= currentCost) return null;
  return { lines, packs };
}

/** Split a list of rope colours into the cheapest packs (Team/Duo/Solo). */
function packColors(input) {
  // Keep same colours together so packs stay as "pure" as possible
  const colors = [...input].sort((x, y) => (x === y ? 0 : x === COLOR_A ? -1 : 1));
  const packs = [];
  let i = 0;
  for (const size of packSizes(colors.length)) {
    const chunk = colors.slice(i, i + size);
    i += size;
    const v = findVariant(size, chunk);
    if (!v) return null;
    packs.push({ variant: v, colors: chunk });
  }
  return packs;
}

function packProperties(pack) {
  if (pack.variant.size === 1) return { Kleur: pack.colors[0] };
  const props = {};
  pack.colors.forEach((c, idx) => (props[`Kleur Touw ${idx + 1}`] = c));
  return props;
}

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return [...ids];
}

const json = (url, body) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json());

let busy = false;
/** Last known cart, so an add-to-cart click can be planned without first asking the server. */
let lastCart = null;

const fetchCart = () => fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

/** Shopify merges an added item into an existing line only when variant and properties are identical. */
function propsKey(props) {
  return JSON.stringify(
    Object.entries(props || {})
      .filter(([, v]) => v !== '' && v !== null && v !== undefined)
      .map(([k, v]) => [k, String(v)])
      .sort(([x], [y]) => (x < y ? -1 : x > y ? 1 : 0))
  );
}
const sameItem = (line, item) =>
  String(line.variant_id) === String(item.id) && propsKey(line.properties) === propsKey(item.properties);

/**
 * Turn "these lines should become these items" into the smallest set of cart calls: lines that already
 * match a wanted item are kept (only their quantity is corrected), the rest is added / removed.
 * Because nothing we add is identical to a line we change, adding first is safe — and a failed add
 * leaves the customer's cart untouched.
 */
function diffOps(lines, items) {
  const wanted = items.map((it) => ({ ...it, quantity: it.quantity || 1 }));
  const updates = {};
  for (const line of lines) {
    let keep = 0;
    for (let i = wanted.length - 1; i >= 0; i--) {
      if (sameItem(line, wanted[i])) keep += wanted.splice(i, 1)[0].quantity;
    }
    if (keep !== line.quantity) updates[line.key] = keep;
  }
  const adds = [];
  for (const it of wanted) {
    const twin = adds.find((x) => String(x.id) === String(it.id) && propsKey(x.properties) === propsKey(it.properties));
    if (twin) twin.quantity += it.quantity;
    else adds.push(it);
  }
  return { updates, adds };
}

/** Run the calls (at most two) and return the fresh cart, including the re-rendered sections. */
async function commit({ updates, adds }) {
  if (adds.length) {
    const added = await json('/cart/add.js', { items: adds });
    if (added.status) throw new Error(added.description || added.message);
  }
  const cart = await json('/cart/update.js', {
    updates,
    sections: sectionIds().join(','),
    sections_url: window.location.pathname,
  });
  if (!Array.isArray(cart.items)) throw new Error(cart.description || cart.message || 'cart update failed');
  lastCart = cart;
  return cart;
}

function announce({ action = 'update', context = 'cart', lines = [], target = document }) {
  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise }));
  return deferred;
}

function settle(deferred, cart, extraDetail = {}) {
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      sections: cart.sections,
      items: cart.items,
      itemCount: cart.item_count,
      source: SOURCE,
      didError: false,
      ...extraDetail,
    },
  });
}

/**
 * Replace the cart lines in `removeKeys` by `items`, and tell the theme (drawer, cart page, cart bubble)
 * through a CartLinesUpdateEvent carrying the re-rendered sections.
 */
async function replaceLines(removeKeys, items) {
  const deferred = announce({ lines: removeKeys.map((id) => ({ id, quantity: 0 })) });
  try {
    const known = () => (lastCart?.items || []).filter((item) => removeKeys.includes(item.key));
    // our picture of the cart is missing or stale (changed in another tab): refresh it once
    if (known().length !== removeKeys.length) lastCart = await fetchCart();
    const cart = await commit(diffOps(known(), items));
    settle(deferred, cart);
    return cart;
  } catch (error) {
    deferred.reject(error);
    lastCart = null;
    console.warn('[volta-bundles]', error);
    throw error;
  }
}

const planItems = (plan) => plan.packs.map((p) => ({ id: p.variant.id, quantity: 1, properties: packProperties(p) }));

/** Repack whatever is in the cart. Pass the cart when it is already known (saves a round trip). */
async function optimize(knownCart) {
  if (busy || byId.size === 0) return;
  busy = true;
  try {
    const cart = knownCart || (await fetchCart());
    lastCart = cart;
    const plan = buildPlan(cart);
    if (!plan) return;
    await replaceLines(
      plan.lines.map((l) => l.key),
      planItems(plan)
    );
  } catch (e) {
    /* logged in replaceLines */
  } finally {
    busy = false;
  }
}

// Per-rope colour picker in the cart (drawer + cart page): swap the line to the matching colour-mix variant.
document.addEventListener('click', async (event) => {
  const swatch = event.target instanceof Element ? event.target.closest('[data-volta-rope-color]') : null;
  if (!swatch || busy) return;
  const box = swatch.closest('.volta-rope-colors');
  if (!box || swatch.classList.contains('is-active')) return;
  event.preventDefault();

  const size = Number(box.dataset.voltaSize);
  const qty = Number(box.dataset.voltaLineQty) || 1;
  const colors = [...box.querySelectorAll('[data-volta-rope]')].map((row) => {
    if (row.contains(swatch)) return swatch.dataset.voltaRopeColor;
    return row.querySelector('.is-active')?.dataset.voltaRopeColor || COLOR_A;
  });
  const variant = findVariant(size, colors);
  if (!variant) return;

  // Instant feedback while the cart updates
  swatch.parentElement.querySelectorAll('[data-volta-rope-color]').forEach((b) => {
    b.classList.toggle('is-active', b === swatch);
    b.setAttribute('aria-pressed', String(b === swatch));
  });
  box.classList.add('is-busy');

  busy = true;
  try {
    await replaceLines([box.dataset.voltaLineKey], [
      { id: variant.id, quantity: qty, properties: packProperties({ variant, colors }) },
    ]);
  } catch (e) {
    box.classList.remove('is-busy');
  } finally {
    busy = false;
  }
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ detail } = {}) => {
      clearAdding();
      if (detail?.didError) closeEarlyDrawer();
      else openedEarly = null;
      if (detail?.source === SOURCE || detail?.didError) return;
      // The theme's own events usually carry the cart lines already: no extra request needed.
      optimize(Array.isArray(detail?.items) ? { items: detail.items } : undefined);
    })
    .catch(() => {
      clearAdding();
      closeEarlyDrawer();
    });
});

// ---- Add to cart --------------------------------------------------------------------------------
// Instant feedback on the button, and when the new rope would be repacked anyway (Solo + Solo → Duo,
// Duo + Solo → Team, …) we put the final packs in the cart straight away instead of first adding the
// loose rope and correcting it afterwards. The drawer then never shows the "wrong" in-between state.
let addingTimer = 0;
function markAdding(form) {
  const btn = form.querySelector('button[name="add"]');
  if (!btn) return;
  btn.classList.add('is-adding');
  btn.setAttribute('aria-busy', 'true');
  clearTimeout(addingTimer);
  addingTimer = setTimeout(clearAdding, 8000);
}
function clearLoader() {
  document.querySelectorAll('cart-drawer-component.volta-cart-loading').forEach((el) => el.classList.remove('volta-cart-loading'));
}
function clearAdding() {
  clearTimeout(addingTimer);
  // The loader only shows while the drawer is still empty, so it can safely stay a moment longer:
  // the theme draws the new content just after the answer arrives, and "empty" must not flash in between.
  setTimeout(clearLoader, 1500);
  document.querySelectorAll('button[name="add"].is-adding').forEach((btn) => {
    btn.classList.remove('is-adding');
    btn.removeAttribute('aria-busy');
  });
}

/**
 * First product in an empty cart: the theme only opens the drawer once the server has answered.
 * Open it right away with a loader instead, so the click feels instant; it fills as soon as the answer is in.
 * (A non-empty drawer is already opened immediately by the theme.)
 */
let openedEarly = null;
function openDrawerEarly(form) {
  const comp = document.querySelector('cart-drawer-component[auto-open]');
  const drawer = comp?.closest('theme-drawer');
  if (!comp || !drawer || typeof drawer.open !== 'function' || drawer.isOpen) return;
  if (!comp.querySelector('.cart-drawer--empty') || form.closest('dialog:modal')) return;
  comp.classList.add('volta-cart-loading');
  openedEarly = drawer;
  drawer.open();
}
/** The add failed: do not leave an empty drawer standing open. */
function closeEarlyDrawer() {
  const drawer = openedEarly;
  openedEarly = null;
  clearLoader();
  if (drawer?.isOpen && drawer.querySelector('.cart-drawer--empty') && typeof drawer.close === 'function') drawer.close();
}

function formProperties(formData) {
  const props = {};
  for (const [name, value] of formData.entries()) {
    const m = /^properties\[(.+)\]$/.exec(name);
    if (m && typeof value === 'string' && value !== '') props[m[1]] = value;
  }
  return props;
}

async function addRepacked(productForm, formData, plan, variant, quantity) {
  busy = true;
  const deferred = announce({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(variant.id), quantity }],
    target: productForm,
  });
  const detail = { sourceId: productForm.id ? String(productForm.id) : undefined, productId: productForm.dataset.productId };
  try {
    let cart;
    try {
      cart = await commit(diffOps(plan.lines, planItems(plan)));
    } catch (error) {
      // Never lose the customer's click: fall back to a plain add of exactly what they chose.
      console.warn('[volta-bundles] repack on add failed, adding as-is', error);
      cart = await commit({ updates: {}, adds: [{ id: variant.id, quantity, properties: formProperties(formData) }] });
    }
    settle(deferred, cart, detail);
    busy = false;
    optimize(cart); // no-op unless the cart differed from what we knew (e.g. changed in another tab)
  } catch (error) {
    busy = false;
    lastCart = null;
    deferred.reject(error);
    clearAdding();
    console.warn('[volta-bundles]', error);
  }
}

// Bound on window (capture) so it runs before Horizon's own submit handling.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    const productForm = form.closest('product-form-component');
    const btn = form.querySelector('button[name="add"]');
    if (!productForm || !btn || btn.disabled) return;
    if (btn.classList.contains('is-adding')) {
      // double click while the first add is still under way
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    markAdding(form);
    openDrawerEarly(form);

    if (busy || !lastCart || byId.size === 0) return; // theme adds it; we repack afterwards if needed
    const formData = new FormData(form);
    const variant = byId.get(String(formData.get('id')));
    if (!variant) return;
    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    const plan = buildPlan(lastCart, [{ variant, quantity }]);
    if (!plan) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    addRepacked(productForm, formData, plan, variant, quantity);
  },
  true
);

// +/− on a Duo/Team line means one rope more/less (Duo + 1 → Team, Duo − 1 → Solo, Team + 1 → Team + Solo),
// instead of Shopify's default of adding a whole extra pack / removing the pack.
// Bound on window (capture) so it runs before Horizon's own document-level capture handler.
window.addEventListener(
  'click',
  async (event) => {
    const btn = event.target instanceof Element
      ? event.target.closest('[on\\:click="/increaseQuantity"], [on\\:click="/decreaseQuantity"]')
      : null;
    if (!btn) return;
    const row = btn.closest('tr, .cart-items__table-row, [data-key]');
    const box = row?.querySelector('.volta-rope-colors');
    if (!box) return;
    // "−" on a Solo simply removes it (theme default). "+" on a Solo goes straight to the Duo below,
    // instead of first showing 2× Solo and correcting it a second later.
    const size = Number(box.dataset.voltaSize);
    const packColorsNow = [...box.querySelectorAll('[data-volta-rope]')].map(
      (r) => r.querySelector('.is-active')?.dataset.voltaRopeColor || COLOR_A
    );
    if (size < 2 && (btn.getAttribute('on:click') !== '/increaseQuantity' || packColorsNow.length !== 1)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;

    const qty = Number(box.dataset.voltaLineQty) || 1;
    const colors = [];
    for (let q = 0; q < qty; q++) colors.push(...packColorsNow);
    if (btn.getAttribute('on:click') === '/increaseQuantity') colors.push(colors[colors.length - 1]);
    else colors.pop();

    const packs = packColors(colors);
    if (!packs) return;
    box.classList.add('is-busy');
    busy = true;
    try {
      await replaceLines(
        [box.dataset.voltaLineKey],
        packs.map((p) => ({ id: p.variant.id, quantity: 1, properties: packProperties(p) }))
      );
    } catch (e) {
      box.classList.remove('is-busy');
    } finally {
      busy = false;
    }
    // Other loose ropes in the cart may now combine into a cheaper pack (e.g. Duo + Solo → Team)
    optimize(lastCart || undefined);
  },
  true
);

// ---- "1 more rope = free shipping" ---------------------------------------------------------------
// With exactly one rope in the cart: a "Make it a Buddy" button in the drawer's shipping bar, and once per
// visit a small pop-up when the customer clicks checkout. Both add a second rope in the same colour, which
// goes straight into the cart as a Buddy (Duo) pack.
const SHIP_COST = Number(document.getElementById('volta-bundle-data')?.dataset.shipCost) || 0;
let UPSELL_TEXT = {};
try {
  UPSELL_TEXT = JSON.parse(document.getElementById('volta-upsell-i18n')?.textContent || '{}');
} catch (e) {
  UPSELL_TEXT = {};
}
const SEEN_KEY = 'volta-upsell-seen';
const money = (cents) => '€' + (cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2).replace('.', ','));

function ropeCount(cart) {
  return (cart?.items || []).reduce((n, item) => {
    const v = byId.get(String(item.variant_id));
    return n + (v ? v.size * item.quantity : 0);
  }, 0);
}

/** Exactly one loose rope in the cart → the Buddy plan and what the second rope really costs. */
function soloUpsell(cart) {
  if (!SHIP_COST || ropeCount(cart) !== 1) return null;
  const line = cart.items.find((item) => byId.get(String(item.variant_id))?.size === 1);
  if (!line) return null;
  const plan = buildPlan(cart, [{ variant: byId.get(String(line.variant_id)), quantity: 1 }]);
  if (!plan) return null;
  // With a discount code we cannot know whether it also applies to the Buddy → no amount is shown then.
  const discounted =
    line.final_line_price < line.original_line_price || (cart.cart_level_discount_applications || []).length > 0;
  const planCost = plan.packs.reduce((sum, p) => sum + p.variant.price, 0);
  const more = planCost - line.final_line_price; // what the cart total goes up by
  const extra = more - SHIP_COST; // net, after the shipping that falls away
  if (discounted) return { plan, more: 0, extra: 0, discounted: true };
  // now / total = the cart total as shown (incl. shipping) before and after the Buddy
  return extra > 0 ? { plan, more, extra, now: cart.total_price + SHIP_COST, total: cart.total_price + more, discounted: false } : null;
}

async function addSecondRope() {
  if (busy) return false;
  busy = true;
  try {
    const cart = lastCart || (await fetchCart());
    lastCart = cart;
    const upsell = soloUpsell(cart);
    if (!upsell) return false;
    await replaceLines(
      upsell.plan.lines.map((l) => l.key),
      planItems(upsell.plan)
    );
    return true;
  } catch (e) {
    return false; /* logged in replaceLines */
  } finally {
    busy = false;
  }
}

// Button in the drawer's shipping bar
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-volta-add-rope]') : null;
  if (!btn) return;
  event.preventDefault();
  if (btn.classList.contains('is-busy')) return;
  btn.classList.add('is-busy');
  btn.setAttribute('aria-busy', 'true');
  rememberUpsellSeen();
  const ok = await addSecondRope();
  if (!ok) {
    btn.classList.remove('is-busy');
    btn.removeAttribute('aria-busy');
  }
});

function upsellSeen() {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1';
  } catch (e) {
    return false;
  }
}
function rememberUpsellSeen() {
  try {
    sessionStorage.setItem(SEEN_KEY, '1');
  } catch (e) {
    /* private mode: the pop-up may show again, which is harmless */
  }
}

const fill = (text, u = {}) =>
  String(text || '')
    .replace('__COST__', money(SHIP_COST))
    .replace('__MORE__', money(u.more || 0))
    .replace('__EXTRA__', money(u.extra || 0))
    .replace('__NOW__', money(u.now || 0))
    .replace('__TOTAL__', money(u.total || 0));

function injectUpsellStyles() {
  if (document.getElementById('volta-upsell-style')) return;
  const style = document.createElement('style');
  style.id = 'volta-upsell-style';
  style.textContent = `
    .volta-upsell { border: 0; padding: 0; margin: auto; width: min(420px, calc(100vw - 32px)); max-height: calc(100dvh - 32px);
      border-radius: 16px; background: #fff; color: #242a31; box-shadow: 0 30px 70px -20px rgb(20 25 32 / .55); overflow: auto; }
    .volta-upsell::backdrop { background: rgb(20 25 32 / .55); }
    .volta-upsell__box { position: relative; display: flex; flex-direction: column; gap: 14px; padding: 30px 26px 24px; text-align: center;
      font-family: 'Barlow', sans-serif; }
    .volta-upsell__bar { width: 52px; height: 6px; margin: 0 auto 2px; border-radius: 3px; background: #88292f; }
    .volta-upsell__title { margin: 0; font-family: 'Anton', sans-serif; font-weight: 400; font-size: 30px; line-height: .98;
      text-transform: uppercase; letter-spacing: .2px; color: #242a31; }
    .volta-upsell__text { margin: 0; font-size: 16.5px; line-height: 1.5; color: #33404d; }
    .volta-upsell__extra { margin: 0; font-family: 'Archivo', sans-serif; font-weight: 800; font-size: 15px; color: #88292f; }
    .volta-upsell__add { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; min-height: 54px; margin-top: 4px;
      padding: 14px 22px; border: 0; border-radius: 999px; background: #88292f; color: #fff; font-family: 'Archivo', sans-serif;
      font-weight: 800; font-size: 16px; cursor: pointer; box-shadow: 0 12px 26px -8px rgb(136 41 47 / .45); }
    .volta-upsell__add:hover { background: #6e2026; }
    .volta-upsell__skip { display: block; width: 100%; min-height: 44px; padding: 10px; border: 0; background: none; color: #596475;
      font-family: 'Archivo', sans-serif; font-weight: 700; font-size: 14px; text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
    .volta-upsell__skip:hover { color: #242a31; }
    .volta-upsell__close { position: absolute; top: 10px; right: 10px; width: 40px; height: 40px; border: 0; border-radius: 999px;
      background: #f3f4f5; color: #242a31; font-size: 18px; line-height: 1; cursor: pointer; }
    .volta-upsell :focus-visible { outline: 2px solid #88292f; outline-offset: 2px; }
    @media (max-width: 749px) {
      /* bottom sheet on phones: thumb-friendly, never covers the whole screen */
      .volta-upsell { width: 100%; max-width: 100%; margin: auto 0 0; border-radius: 20px 20px 0 0; max-height: 85dvh; }
      .volta-upsell__box { padding: 28px 20px calc(18px + env(safe-area-inset-bottom)); }
      .volta-upsell__title { font-size: 28px; }
      .volta-upsell[open] { animation: volta-upsell-up .25s ease-out; }
    }
    @keyframes volta-upsell-up { from { transform: translateY(100%); } to { transform: none; } }
    @media (prefers-reduced-motion: reduce) { .volta-upsell[open] { animation: none; } }
  `;
  document.head.appendChild(style);
}

let upsellDialog = null;
/** Shows the pop-up; resolves 'add', 'skip' or 'close'. */
function showUpsell(u) {
  const discounted = u.discounted;
  injectUpsellStyles();
  if (!upsellDialog) {
    upsellDialog = document.createElement('dialog');
    upsellDialog.className = 'volta-upsell';
    upsellDialog.setAttribute('aria-labelledby', 'volta-upsell-title');
    document.body.appendChild(upsellDialog);
  }
  const t = UPSELL_TEXT;
  upsellDialog.innerHTML = '';
  const box = document.createElement('div');
  box.className = 'volta-upsell__box';
  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    n.className = cls;
    if (text !== undefined) n.textContent = text;
    return n;
  };
  const close = el('button', 'volta-upsell__close', '✕');
  close.type = 'button';
  close.setAttribute('aria-label', t.close || 'Sluiten');
  const title = el('h2', 'volta-upsell__title', t.title || 'Nog 1 touw = gratis verzending');
  title.id = 'volta-upsell-title';
  const add = el(
    'button',
    'volta-upsell__add',
    discounted ? t.addPlain || 'Ja, voeg een 2e touw toe' : fill(t.add || 'Ja, voeg een 2e touw toe (+__EXTRA__)', u)
  );
  add.type = 'button';
  const skip = el('button', 'volta-upsell__skip', fill(t.skip || 'Nee bedankt, ik betaal __COST__ verzending', u));
  skip.type = 'button';
  box.append(
    close,
    el('span', 'volta-upsell__bar'),
    title,
    el(
      'p',
      'volta-upsell__text',
      discounted
        ? fill(t.textPlain || 'Neem er een tweede touw bij en de __COST__ verzending vervalt.', u)
        : fill(t.text || 'Neem er een tweede touw bij voor maar __EXTRA__ extra, want de __COST__ verzending vervalt.', u)
    ),
    ...(discounted ? [] : [el('p', 'volta-upsell__extra', fill(t.extra || 'Je totaal gaat van __NOW__ naar __TOTAL__.', u))]),
    add,
    skip
  );
  upsellDialog.appendChild(box);

  return new Promise((resolve) => {
    let result = 'close';
    const done = (r) => {
      result = r;
      upsellDialog.close();
    };
    add.addEventListener('click', () => done('add'));
    skip.addEventListener('click', () => done('skip'));
    close.addEventListener('click', () => done('close'));
    upsellDialog.addEventListener('click', (e) => {
      if (e.target === upsellDialog) done('close');
    });
    upsellDialog.addEventListener('close', () => resolve(result), { once: true });
    upsellDialog.showModal();
    // focus for keyboard/screen-reader users, without drawing the focus ring for mouse/touch users
    add.focus({ focusVisible: false });
  });
}

// Checkout click with exactly one rope: offer the second rope once per visit.
// Bound on window (capture) before the company-details checkout guard, which still runs on "continue".
let passThroughCheckout = false;
window.addEventListener(
  'click',
  async (event) => {
    const btn = event.target instanceof Element ? event.target.closest('#checkout, [name="checkout"]') : null;
    if (!btn || passThroughCheckout || btn.disabled || upsellSeen() || busy) return;
    const upsell = lastCart && soloUpsell(lastCart);
    if (!upsell) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    rememberUpsellSeen();
    const choice = await showUpsell(upsell);
    if (choice === 'add') {
      await addSecondRope();
    } else if (choice === 'skip') {
      passThroughCheckout = true;
      btn.click();
      setTimeout(() => (passThroughCheckout = false), 1500);
    }
  },
  true
);

// Carts built before this script existed (or in another tab) get repacked on the next page view.
optimize();
